import Number1Icon from '@/assets/icons/number_1_icon.svg';
import Number2Icon from '@/assets/icons/number_2_icon.svg';
import Number3Icon from '@/assets/icons/number_3_icon.svg';
import GoldCoins1 from '@/assets/images/gold_coins_1.png';
import {
  Button,
  Card,
  Dialog,
  HSpacer,
  IconButton,
  Text,
  TopAppBar,
  VSpacer,
} from '@/components/DesignSystem';
import { AboutAccountRolesDialog } from '@/components/shared/AboutAccountRolesDialog';
import { Routes } from '@/constants/Routes';
import { useDashboard } from '@/hooks/useDashboard';
import { useDashboardNavigate } from '@/pages/CustomerDashboard/helpers';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Groups from '@mui/icons-material/Groups';
import HelpCenter from '@mui/icons-material/HelpCenter';
import { CircularProgress, Container, Divider, Stack, useTheme } from '@mui/material';
import { AccountGroupRole } from '@shared/enums';
import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

const infoCardStyle = {
  borderRadius: '8px',
  boxShadow: '0px 6px 12px -6px rgba(24, 39, 75, 0.12),' +
    '0px 8px 24px -4px rgba(24, 39, 75, 0.08);',
  padding: '0px',
  position: 'relative',
};

export const HowItWorksPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const navigateToPage = useDashboardNavigate();
  const [showLoyaltyInfoModal, setShowLoyaltyInfoModal] = useState(false);
  const [showAboutRolesModal, setShowAboutRolesModal] = useState(false);

  const { data: dashboardData } = useDashboard(accessToken);
  const brandColor = dashboardData?.retailer.brandColor ?? '#97743a';

  if (!dashboardData) {
    return (
      <Container>
        <Stack alignItems="center">
          <VSpacer size="14" />
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  const {
    farmer,
    linkedAccounts,
    retailer,
  } = dashboardData;

  const Header = (
    <TopAppBar
      isStatic
      leftAccessory={(
        <IconButton
          onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD, accessToken)}
          sx={{ color: 'white' }}
          testID="back-button"
        >
          <ArrowBack />
        </IconButton>
      )}
      rightAccessory={<HSpacer size="7" />}
      sx={{ bgcolor: brandColor }}
      testID="top-app-bar"
    >
      <Text category="title-large" sx={{ color: 'white' }}>
        {t($ => $.customerDashboard.howItWorks.title)}
      </Text>
    </TopAppBar>
  );

  const LoyaltyInfoModal = (
    <Dialog
      cancelButton={(props) => (
        <Button
          onClick={() => setShowLoyaltyInfoModal(false)}
          sx={{
            border: `1px solid ${brandColor}`,
            color: brandColor,
            p: '10px 16px',
          }}
          variant="outlined"
          {...props}
        >
          {t($ => $.common.actions.close)}
        </Button>
      )}
      onClose={() => setShowLoyaltyInfoModal(false)}
      open
      showCloseButton={false}
      testID="loyalty-info-modal"
      title={t($ => $.customerDashboard.howItWorks.sections.whatArePoints)}
    >
      <Text category="title-medium">
        {t($ => $.customerDashboard.howItWorks.modal.pointWorth)}
      </Text>
      <Text category="body-large">
        {t($ => $.customerDashboard.howItWorks.modal.pointValue)}
      </Text>
      <VSpacer size="5" />
      <Text category="title-medium">
        {t($ => $.customerDashboard.howItWorks.modal.howToEarn)}
      </Text>
      <Text category="body-large">
        {t($ => $.customerDashboard.howItWorks.modal.earnDescription)}
      </Text>
      <VSpacer size="5" />
      <Text category="title-medium">
        {t($ => $.customerDashboard.howItWorks.modal.howToUse)}
      </Text>
      <Text category="body-large">
        {t($ => $.customerDashboard.howItWorks.modal.useDescription, {
          retailerName: retailer.name,
        })}
      </Text>
      <VSpacer size="5" />
      <Divider />
      <VSpacer size="3" />
      <Text category="body-xsmall">
        {t($ => $.customerDashboard.howItWorks.modal.disclaimer)}
      </Text>
    </Dialog>
  );

  return  (
    <>
      <Stack
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
      >
        {Header}
        <Stack gap="16px" p="16px">
          <InfoCard
            brandColor={brandColor}
            description={t(($) => $.customerDashboard.howItWorks.infoCards.earnPoints.description)}
            icon={Number1Icon}
            title={t(($) => $.customerDashboard.howItWorks.infoCards.earnPoints.title)}
          />
          <InfoCard
            brandColor={brandColor}
            description={
              <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                <Trans
                  components={{ bold: <span style={{ fontWeight: 500 }} /> }}
                  i18nKey={
                    ($) => $.customerDashboard.howItWorks.infoCards.redeemRewards.description
                  }
                />
              </Text>
            }
            icon={Number2Icon}
            title={t(($) => $.customerDashboard.howItWorks.infoCards.redeemRewards.title)}
          />
          <InfoCard
            brandColor={brandColor}
            description={t(
              ($) => $.customerDashboard.howItWorks.infoCards.increaseBenefits.description,
            )}
            icon={Number3Icon}
            title={t(($) => $.customerDashboard.howItWorks.infoCards.increaseBenefits.title)}
          />
          <VSpacer size="1" />
          <Stack
            borderRadius="8px"
            boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.30), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)"
            overflow="hidden"
            position="relative"
          >
            <Stack
              height="100%"
              position="absolute"
              width="100%"
            >
              <img
                src={GoldCoins1}
                style={{
                  left: "-45px",
                  position: "absolute",
                  transform: "rotate(-22deg)",
                  transformOrigin: "top left",
                  top: "50px",
                }}
              />
            </Stack>
            <Stack
              alignItems="center"
              alignSelf="stretch"
              direction="row"
              justifyContent="space-between"
              p="8px 16px"
              sx={{
                background:
                  'radial-gradient(88.19% 128.14% at 45.92% 29.08%, #FFF 17.96%, #FFFFF1 100%)',
              }}
            >
              <Stack>
                <Text category="body-xlarge" fontWeight={500}>
                  {t($ => $.customerDashboard.howItWorks.sections.whatArePoints)}
                </Text>
                <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                  {t($ => $.customerDashboard.howItWorks.sections.whatArePointsDescription)}
                </Text>
              </Stack>
              <HSpacer size="5" />
              <Button
                onClick={() => setShowLoyaltyInfoModal(true)}
                size="small"
                square
                sx={{ '&:hover': { bgcolor: brandColor }, bgcolor: brandColor }}
                testID="learn-more-button"
              >
                {t($ => $.common.actions.learnMore)}
              </Button>
            </Stack>
          </Stack>

        </Stack>
        <VSpacer size="1" />
        <Stack gap="8px" p="8px 16px 16px">
          <Divider />
          <Button
            endIcon={<ChevronRight />}
            onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_FAQ, accessToken)}
            startIcon={<HelpCenter />}
            sx={{
              background: '#FFFFFF',
              color: 'var(--on-surface-main, rgba(0, 0, 0, 0.96))',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.5px',
              borderRadius: '0px',
              boxShadow: 'none',
              justifyContent: 'flex-start',
              textAlign: 'left',
              padding: '16px',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
              },
              '& .MuiButton-startIcon > svg': {
                fontSize: '24px',
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
              },
              '&:hover': {
                background: '#FFFFFF',
                boxShadow: 'none',
              },
            }}
            testID="faq-button"
          >
            {t($ => $.customerDashboard.howItWorks.buttons.frequentlyAskedQuestions)}
          </Button>
          <Divider />
          <Button
            endIcon={<ChevronRight />}
            onClick={() => setShowAboutRolesModal(true)}
            startIcon={<Groups />}
            sx={{
              background: '#FFFFFF',
              color: 'var(--on-surface-main, rgba(0, 0, 0, 0.96))',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.5px',
              borderRadius: '0px',
              boxShadow: 'none',
              justifyContent: 'flex-start',
              textAlign: 'left',
              padding: '16px',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
              },
              '& .MuiButton-startIcon > svg': {
                fontSize: '24px',
              },
              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
              },
              '&:hover': {
                background: '#FFFFFF',
                boxShadow: 'none',
              },
            }}
            testID="about-account-roles-button"
          >
            {t($ => $.customerDashboard.howItWorks.buttons.aboutAccountRoles)}
          </Button>
          <Divider />
        </Stack>
          <VSpacer size="3" />
      </Stack>
      {showLoyaltyInfoModal && LoyaltyInfoModal}
      {showAboutRolesModal && (
        <AboutAccountRolesDialog
          brandColor={brandColor}
          customerRole={farmer.accountGroupRole as AccountGroupRole}
          linkedAccounts={linkedAccounts}
          onClose={() => setShowAboutRolesModal(false)}
        />
      )}
    </>
  );
};

interface InfoCardProps {
  brandColor: string,
  description: React.ReactNode,
  icon: string,
  title: string,
}

const InfoCard = ({
  brandColor,
  description,
  icon,
  title,
}: InfoCardProps) => {
  const theme = useTheme();
  return (
    <Card
      cardContentStyle={{ p: '16px 20px 18px 16px' }}
      sx={infoCardStyle}
      testID={`info-card-${title}`}
    >
      <Stack direction="row">
        <Stack
          alignItems="center"
          bgcolor={brandColor}
          borderRadius="480px"
          justifyContent="flex-end"
          maxHeight="32px"
          maxWidth="32px"
          minHeight="32px"
          minWidth="32px"
          p="8px 0px 0px 9.953px"
        >
          <img src={icon} />
        </Stack>
        <HSpacer size="5" />
        <Stack>
          <Text category="title-large">
            {title}
          </Text>
          <VSpacer size="1" />
          <Stack direction="row" justifyContent="space-between">
            {typeof description === 'string' ? (
              <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                {description}
              </Text>
            ) : description}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
